import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import Seo from "../../components/Seo";

const inp = "w-full px-3 py-2 rounded-lg border border-slate-200 text-sm";
function F({ label, k, f, set, ph }) {
  return <div><label className="text-sm font-medium text-slate-700">{label}</label><input value={f[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} className={`${inp} mt-1.5`} data-testid={`set-${k}`} /></div>;
}

export default function AdminSettings() {
  const [f, setF] = useState({});
  const { data } = useQuery({ queryKey: ["settings-admin"], queryFn: async () => (await api.get("/settings")).data });
  useEffect(() => { if (data) setF(data); }, [data]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => { try { const { id, ...body } = f; await api.put("/admin/settings", body); toast.success("Settings saved"); } catch { toast.error("Failed (super admin only)"); } };

  return (
    <div className="max-w-3xl">
      <Seo title="Settings | Admin" />
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Settings</h1>
      <p className="text-slate-500">Configure contact details, social links and SEO/tracking.</p>

      <section className="bg-white rounded-2xl border border-slate-200 p-6 mt-6 space-y-4">
        <h2 className="font-heading font-bold text-slate-900">Contact & Business</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <F label="Site Name" k="site_name" f={f} set={set} />
          <F label="Phone (tel:)" k="phone" f={f} set={set} ph="+919999999999" />
          <F label="WhatsApp Number (no +)" k="whatsapp" f={f} set={set} ph="919999999999" />
          <F label="Email" k="email" f={f} set={set} />
          <F label="Office Address" k="office_address" f={f} set={set} />
          <F label="Google Maps Embed URL" k="map_embed" f={f} set={set} />
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 p-6 mt-6 space-y-4">
        <h2 className="font-heading font-bold text-slate-900">Social Links</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <F label="Facebook" k="facebook" f={f} set={set} />
          <F label="Instagram" k="instagram" f={f} set={set} />
          <F label="YouTube" k="youtube" f={f} set={set} />
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 p-6 mt-6 space-y-4">
        <h2 className="font-heading font-bold text-slate-900">SEO & Tracking</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <F label="Search Console Code" k="gsc_verification" f={f} set={set} />
          <F label="GA Measurement ID" k="ga_measurement_id" f={f} set={set} />
          <F label="GTM ID" k="gtm_id" f={f} set={set} />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 pt-2"><Switch checked={!!f.auto_publish_blog} onCheckedChange={(v) => set("auto_publish_blog", v)} data-testid="set-auto-publish" /> Auto-publish AI blogs (kept OFF by default)</label>
      </section>

      <Button onClick={save} className="bg-indigo-900 hover:bg-indigo-800 mt-6" data-testid="settings-save"><Save className="w-4 h-4 mr-2" /> Save Settings</Button>
    </div>
  );
}
